import { useState } from 'react'
import { Link, useSearchParams, useLocation } from 'react-router-dom'
import { gql } from 'graphql-request'
import { Loader2 } from 'lucide-react'

import { useQuery } from '@hooks/graphql'
import { formatTimestamp } from '@helpers/format'
import PageHeading from '@components/PageHeading'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import Pagination from '@components/Pagination'
import SearchInput from '@components/SearchInput'
import { startCase } from 'lodash'
import NoResults from './NoResults'

const ALL_STATUSES_VALUE = "ALL_STATUSES_FILTER" // Define a constant for clarity

const ORGANIZATIONS_QUERY = gql`
  query organizations($page: Int!, $search: String, $accountStatus: AccountStatusEnum) {
    organizations(page: $page, perPage: 10, search: $search, accountStatus: $accountStatus) {
      nodes {
        id
        name
        createdAt
        accountStatus
      }
      pagesCount
    }
  }
`

const OrganizationList = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const page = Number(searchParams.get('page')) || 1
  const search = searchParams.get('search') || ''
  const accountStatus = searchParams.get('accountStatus') || ''
  const [searchTerm, setSearchTerm] = useState(search)

  const { data: { organizations: { nodes: organizations = [], pagesCount } = {} } = {}, isLoading } = useQuery({
    queryKey: ['organizations', page, search, accountStatus],
    gqlQuery: ORGANIZATIONS_QUERY,
    variables: { page, search, accountStatus: accountStatus || null }
  })

  const updateParams = newParams => {
    const params = {
      page,
      search,
      accountStatus,
      ...newParams
    }

    // Ensure consistent empty string for no filter
    if (params.accountStatus === ALL_STATUSES_VALUE) {
      params.accountStatus = ''
    }

    if (params.page === 1) delete params.page
    if (params.search === '') delete params.search
    if (params.accountStatus === '') delete params.accountStatus

    setSearchParams(params)
  }

  const clearFilters = () => {
    setSearchTerm('')
    setSearchParams({})
  }

  const clearSearch = () => {
    setSearchTerm('')
    updateParams({ search: '', page: 1 })
  }

  const getBadgeVariant = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'default' // Or a specific green variant if defined
      case 'TRIAL':
        return 'secondary' // Or a specific purple variant if defined
      case 'DORMANT':
        return 'destructive' // Or a specific red variant if defined
      case 'TEST_DATA':
        return 'outline'
      default:
        return 'outline'
    }
  }

  return (
    <>
      <PageHeading title='Organizations' />

      <div className='m-5'>
        <div className='flex gap-3 mb-5'>
          <SearchInput
            className='flex-1'
            value={searchTerm}
            onChange={event => setSearchTerm(event.target.value)}
            onSearch={() => updateParams({ search: searchTerm, page: 1 })}
            onClear={clearSearch}
          />

          <Select
            value={accountStatus === '' ? ALL_STATUSES_VALUE : accountStatus}
            onValueChange={value => updateParams({ accountStatus: value, page: 1 })}
          >
            <SelectTrigger className="w-[280px] h-10">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_STATUSES_VALUE}>All Statuses</SelectItem>
              <SelectItem value='ACTIVE'>Active</SelectItem>
              <SelectItem value='TRIAL'>Trial</SelectItem>
              <SelectItem value='DORMANT'>Dormant</SelectItem>
              <SelectItem value='TEST_DATA'>Test Data</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Choose>
          <When condition={isLoading}>
            <div className='flex justify-center mt-64'>
              <Loader2 className='h-8 w-8 animate-spin' />
            </div>
          </When>

          <When condition={organizations.length < 1 && !isLoading}>
            <NoResults clearFilters={clearFilters} />
          </When>

          <Otherwise>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead width='40%'>Name</TableHead>
                  <TableHead width='30%'>Created at</TableHead>
                  <TableHead width='30%'>Status</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                <For each='organization' of={organizations}>
                  <TableRow key={organization.id}>
                    <TableCell>
                      <Link
                        className='block'
                        to={`${organization.id}`}
                        state={{ previousListParams: location.search }}
                      >
                        {organization.name}
                      </Link>
                    </TableCell>

                    <TableCell>
                      <Link
                        className='block'
                        to={`${organization.id}`}
                        state={{ previousListParams: location.search }}
                      >
                        {formatTimestamp(organization.createdAt)}
                      </Link>
                    </TableCell>

                    <TableCell>
                      <Link
                        className='block'
                        to={`${organization.id}`}
                        state={{ previousListParams: location.search }}
                      >
                        <Badge variant={getBadgeVariant(organization.accountStatus)}>
                          {!organization.accountStatus ? 'NONE' : startCase(organization.accountStatus)}
                        </Badge>
                      </Link>
                    </TableCell>
                  </TableRow>
                </For>
              </TableBody>
            </Table>

            <Pagination page={page} pagesCount={pagesCount} setPage={newPage => updateParams({ page: newPage })} />
          </Otherwise>
        </Choose>
      </div>
    </>
  )
}

export default OrganizationList
